import {ApolloClient, ApolloLink, InMemoryCache, HttpLink, split} from '@apollo/client';
import {onError} from '@apollo/client/link/error';
import {getMainDefinition} from '@apollo/client/utilities';
import {WebSocketLink} from 'apollo-link-ws';
import {SubscriptionClient} from 'subscriptions-transport-ws';
import axios from 'axios';
import {wsLogStore} from '../utils/wsLogStore';
import {handleApolloError} from './errorLink';

// Constants
// import { endpoints } from '@collabra/cway-frontend-common/constants';
import possibleTypes from './possibleTypes';

// Local state
import {localTypePolicies} from './localState';

import {EnvironmentService} from '../config/environment';

// HTTP GraphQL goes through Vite proxy
// WebSocket setup: Use localhost proxy in development, direct connection in production
const isDevelopment = window.location.hostname === 'localhost';
const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

const endpoints = {
  graphql: import.meta.env.VITE_GRAPHQL_ENDPOINT || '/graphql',
  ws: isDevelopment
    ? `${wsProtocol}//${window.location.host}/subscriptions` // Dev: proxy through Vite
    : `${EnvironmentService.getWsUrl()}/subscriptions`, // Prod: direct to backend
};

console.log('[Apollo] Initializing with endpoints:', {
  graphql: endpoints.graphql,
  ws: endpoints.ws,
  environment: EnvironmentService.get(),
  isDevelopment,
  note: isDevelopment ? 'WebSocket proxied through Vite' : 'WebSocket direct to backend',
});

const cache = new InMemoryCache({
  possibleTypes,
  typePolicies: localTypePolicies,
});

// HTTP link for GraphQL queries with credentials (cookies)
const httpLink = new HttpLink({
  uri: endpoints.graphql,
  credentials: 'include', // Send cookies with requests
});

// Add app identification and auth header to every request
const appHeaderLink = new ApolloLink((operation, forward) => {
  // Get Authorization header from axios (set after login)
  const authHeader = axios.defaults.headers.common['Authorization'];
  const tokenFromStorage = localStorage.getItem('access_token');

  console.log('[Apollo] Request:', operation.operationName);
  console.log('[Apollo] Auth header from axios:', authHeader ? 'present' : 'missing');
  console.log('[Apollo] Token from localStorage:', tokenFromStorage ? 'present' : 'missing');

  // Prefer token from localStorage if axios header is missing
  const finalAuthHeader = authHeader || (tokenFromStorage ? `Bearer ${tokenFromStorage}` : null);

  if (finalAuthHeader) {
    console.log('[Apollo] Using auth header:', finalAuthHeader.substring(0, 20) + '...');
  } else {
    console.log('[Apollo] No auth header available');
  }

  operation.setContext(({headers}) => ({
    headers: {
      ...headers,
      'X-Calling-App': import.meta.env.VITE_CWAY_APPLICATION || 'cway-admin',
      ...(finalAuthHeader ? {Authorization: finalAuthHeader} : {}),
    },
  }));
  return forward(operation);
});

// Handle GraphQL and network errors
const errorLink = onError(handleApolloError);

// Create WebSocket client for subscriptions (using old library for server compatibility)
// Authentication via cookies (main_token, main_refresh_token) - same as desktop
// Cookies are sent automatically by the browser with WebSocket upgrade request
const subscriptionClient = new SubscriptionClient(endpoints.ws, {
  reconnect: true,
  connectionCallback: (error) => {
    if (error) {
      console.error('[WebSocket] ⚠️ Connection error:', error);
      wsLogStore.addLog('error', error);
    } else {
      console.log('[WebSocket] ✅ Connected successfully with cookies');
      wsLogStore.addLog('connected');
    }
  },
  // Use native WebSocket for browser environment
  webSocketImpl: WebSocket,
});

// Add event listeners for logging
subscriptionClient.onConnecting(() => {
  console.log('[WebSocket] 🔄 Connecting...');
  wsLogStore.addLog('connecting');
});

subscriptionClient.onConnected(() => {
  console.log('[WebSocket] 🔓 Connected');
  wsLogStore.addLog('connected');
});

subscriptionClient.onReconnecting(() => {
  console.log('[WebSocket] 🔄 Reconnecting...');
  wsLogStore.addLog('connecting');
});

subscriptionClient.onReconnected(() => {
  console.log('[WebSocket] ✅ Reconnected');
  wsLogStore.addLog('connected');
});

subscriptionClient.onDisconnected(() => {
  console.log('[WebSocket] ❌ Disconnected');
  wsLogStore.addLog('closed', {reason: 'Disconnected'});
});

subscriptionClient.onError((error) => {
  console.error('[WebSocket] ⚠️ Error:', error);
  wsLogStore.addLog('error', error);
});

// Intercept WebSocket messages for logging
// Access the underlying WebSocket client after connection
let wsClient: WebSocket | null = null;
const originalConnect = subscriptionClient.connect.bind(subscriptionClient);
subscriptionClient.connect = function () {
  originalConnect();

  // Wait a bit for the WebSocket to be created
  setTimeout(() => {
    // @ts-ignore - Access private client property
    wsClient = subscriptionClient.client;

    if (wsClient) {
      const originalOnMessage = wsClient.onmessage;
      wsClient.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[WebSocket] 📨 Received:', data);
          wsLogStore.addLog('message', data);
        } catch (e) {
          console.log('[WebSocket] 📨 Received (raw):', event.data);
          wsLogStore.addLog('message', event.data);
        }

        if (originalOnMessage) {
          originalOnMessage.call(wsClient, event);
        }
      };
    }
  }, 100);
};

// Create WebSocket link for subscriptions
const wsLink = new WebSocketLink(subscriptionClient);

// Split link: subscriptions go through WebSocket, queries/mutations through HTTP
const splitLink = split(
  ({query}) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  ApolloLink.from([appHeaderLink, httpLink]),
);

// Initialize Apollo client
const client = new ApolloClient({
  link: ApolloLink.from([errorLink, splitLink]),
  cache,
});

export default client;
